import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useCart } from '../../../contexts/CartContext';
import { likeProduct, addToCart } from '../../../service/ShopService';
import styles from './ProductInfo.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHeart, 
  faShare, 
  faStar, 
  faPlus, 
  faMinus, 
  faShoppingCart,
  faTag,
  faTruck,
  faShieldAlt,
  faUndo,
  faCheck,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

const ProductInfo = ({ product, onFavoriteToggle, isFavorite, discount, formatPrice }) => {
  const { user } = useAuth();
  const { addToCart: addToCartContext } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLiking, setIsLiking] = useState(false);

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= (product?.stock || 999)) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = async () => {
    if (!product) return;

    try {
      await addToCart(product.slug, quantity);
      addToCartContext(product, { quantity, type: 'product' });
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Ошибка при добавлении в корзину');
    }
  };

  const handleToggleFavorite = async () => {
    if (!user) {
      alert('Войдите в аккаунт для добавления в избранное');
      return;
    }

    if (!product) return;

    setIsLiking(true);
    try {
      const response = await likeProduct(product.slug);
      onFavoriteToggle && onFavoriteToggle(response.data.liked);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsLiking(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: product.description,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Ошибка при попытке поделиться:', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Ссылка скопирована в буфер обмена!');
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <FontAwesomeIcon key={i} icon={faStar} className={styles.starFilled} />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <FontAwesomeIcon key="half" icon={faStar} className={styles.starHalf} />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <FontAwesomeIcon key={`empty-${i}`} icon={faStar} className={styles.starEmpty} />
      );
    }

    return stars;
  };

  if (!product) {
    return <div className={styles.loading}>Загрузка информации о товаре...</div>;
  }

  const isInStock = product.stock > 0;
  const currentPrice = product.discount_price || product.price;
  const originalPrice = product.discount_price ? product.price : null;

  return (
    <div className={styles.productInfo}>
      {/* Заголовок и рейтинг */}
      <div className={styles.header}>
        <h1 className={styles.title}>{product.name}</h1>
        <div className={styles.actions}>
          <button 
            className={`${styles.actionButton} ${isFavorite ? styles.favorite : ''} ${isLiking ? styles.loading : ''}`}
            onClick={handleToggleFavorite}
            disabled={isLiking}
            title={isFavorite ? 'Удалить из избранного' : 'Добавить в избранное'}
          >
            <FontAwesomeIcon icon={faHeart} />
          </button>
          <button 
            className={styles.actionButton}
            onClick={handleShare}
            title="Поделиться"
          >
            <FontAwesomeIcon icon={faShare} />
          </button>
        </div>
      </div>

      {/* Рейтинг и отзывы */}
      <div className={styles.rating}>
        <div className={styles.stars}>
          {renderStars(product.rating_avg || 0)}
        </div>
        <span className={styles.ratingValue}>
          {(product.rating_avg || 0).toFixed(1)}
        </span>
        <span className={styles.reviewsCount}>
          ({product.reviews?.length || 0} отзывов)
        </span>
      </div>

      {/* Цена */}
      <div className={styles.pricing}>
        <div className={styles.currentPrice}>
          {formatPrice(currentPrice)} Сом
        </div>
        {originalPrice && (
          <div className={styles.oldPrice}>
            {formatPrice(originalPrice)} Сом
          </div>
        )}
        {discount > 0 && (
          <div className={styles.discount}>
            <FontAwesomeIcon icon={faTag} />
            -{discount}%
          </div>
        )}
      </div>

      {/* Краткое описание */}
      {product.short_description && (
        <div className={styles.shortDescription}>
          {product.short_description}
        </div>
      )}

      {/* Наличие */}
      <div className={styles.availability}>
        <div className={`${styles.status} ${isInStock ? styles.inStock : styles.outOfStock}`}>
          {isInStock ? (
            <>
              <FontAwesomeIcon icon={faCheck} />
              В наличии
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              Нет в наличии
            </>
          )}
        </div>
        {isInStock && (
          <span className={styles.stockInfo}>
            Осталось: {product.stock} шт.
          </span>
        )}
      </div>

      {/* Выбор количества и добавление в корзину */}
      {isInStock && (
        <div className={styles.purchase}>
          <div className={styles.quantitySelector}>
            <span className={styles.quantityLabel}>Количество:</span>
            <div className={styles.quantityControls}>
              <button 
                className={styles.quantityButton}
                onClick={() => handleQuantityChange(-1)}
                disabled={quantity <= 1}
              >
                <FontAwesomeIcon icon={faMinus} />
              </button>
              <span className={styles.quantity}>{quantity}</span>
              <button 
                className={styles.quantityButton}
                onClick={() => handleQuantityChange(1)}
                disabled={quantity >= product.stock}
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>
          </div>

          <button 
            className={styles.addToCartButton}
            onClick={handleAddToCart}
          >
            <FontAwesomeIcon icon={faShoppingCart} />
            Добавить в корзину
          </button>

          {showSuccess && (
            <div className={styles.successMessage}>
              <FontAwesomeIcon icon={faCheck} />
              Товар добавлен в корзину!
            </div>
          )}
        </div>
      )}

      {/* Общая стоимость */}
      {isInStock && (
        <div className={styles.totalPrice}>
          <span>Общая стоимость:</span>
          <strong>{formatPrice(currentPrice * quantity)} Сом</strong>
        </div>
      )}

      {/* Дополнительная информация */}
      <div className={styles.extraInfo}>
        <div className={styles.infoItem}>
          <FontAwesomeIcon icon={faTruck} />
          <div>
            <strong>Доставка</strong>
            <p>Бесплатная доставка от 5000 Сом</p>
          </div>
        </div>
        <div className={styles.infoItem}>
          <FontAwesomeIcon icon={faShieldAlt} />
          <div>
            <strong>Гарантия</strong>
            <p>Официальная гарантия производителя</p>
          </div>
        </div>
        <div className={styles.infoItem}>
          <FontAwesomeIcon icon={faUndo} />
          <div>
            <strong>Возврат</strong>
            <p>Возврат товара в течение 14 дней</p>
          </div>
        </div>
      </div>

      {/* Артикул и категория */}
      <div className={styles.metadata}>
        <div className={styles.metaItem}>
          <strong>Категория:</strong> {product.category?.name || 'Не указана'}
        </div>
        {product.created_at && (
          <div className={styles.metaItem}>
            <strong>Добавлен:</strong> {new Date(product.created_at).toLocaleDateString('ru-RU')}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductInfo;